import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Textarea, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import type { WorkflowDetail } from "../types";
import { isPending, runView } from "./regression-model";
import { RunCard } from "./regression-run-card";
import { useLatestRun, useRegressionCases, useRegressionMutations } from "./use-regression";

const R = "engage.workflows.regression";
const WHY = new Set(["no_run", "running", "failed", "stale"]);

interface PublishDialogProps {
  workflowId: string;
  /** `details.reason` of the 409: why publishing is held back. */
  reason: string | null;
  /** The member holds `workflows.publish_override`. */
  canOverride: boolean;
  onPublished: (detail: WorkflowDetail) => void;
  onClose: () => void;
}

/** Publishing is held by the test cases: run them three times, then publish; or (with the permission) publish anyway with a reason. */
export function PublishDialog({ workflowId, reason, canOverride, onPublished, onClose }: PublishDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const cases = useRegressionCases(workflowId);
  const latest = useLatestRun(workflowId);
  const mutations = useRegressionMutations(workflowId);
  const [why, setWhy] = useState("");
  const [missing, setMissing] = useState(false);
  const view = runView(latest.data);
  const running = isPending(latest.data) || mutations.run.isPending;
  const passed = view === "passed";
  const fail = (error: unknown) => { toast.error(isApiError(error) && error.code === "regression_not_passed" ? t(`${R}.publish.why.${reason ?? "no_run"}`) : message(error)); };
  const publish = (override: { reason: string } | null) => {
    mutations.publish.mutate(override, {
      onSuccess: (detail) => { toast.success(t(override ? `${R}.publish.overridden` : `${R}.publish.published`, { version: detail.live_version ?? 1 })); onPublished(detail); },
      onError: fail,
    });
  };
  const override = () => {
    const text = why.trim();
    setMissing(text.length === 0);
    if (text.length > 0) publish({ reason: text });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${R}.publish.title`)}
        description={reason !== null && WHY.has(reason) ? t(`${R}.publish.why.${reason}`) : t(`${R}.publish.body`)}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>{t(`${R}.publish.close`)}</Button>
            <Button variant="secondary" loading={running} onClick={() => { mutations.run.mutate(undefined, { onError: (error) => { toast.error(message(error)); } }); }}><Icon name="play" aria-hidden />{t(`${R}.publish.runAgain`)}</Button>
            <Button disabled={!passed || running} loading={mutations.publish.isPending && !why} onClick={() => { publish(null); }}>{t(`${R}.publish.publish`)}</Button>
          </>
        }
      >
        <div className="space-y-5">
          <p className="text-sm text-fg-muted">{t(`${R}.publish.body`)}</p>
          <RunCard run={latest.isError ? null : latest.data} cases={cases.data?.items ?? []} />
          <section className="space-y-2 rounded-card border border-line p-3" aria-label={t(`${R}.publish.overrideTitle`)}>
            <h3 className="text-sm font-semibold">{t(`${R}.publish.overrideTitle`)}</h3>
            {canOverride ? (
              <>
                <p className="text-xs text-fg-muted">{t(`${R}.publish.overrideBody`)}</p>
                <Field label={t(`${R}.publish.reason`)} hint={t(`${R}.publish.reasonHint`)} error={missing ? t(`${R}.publish.reasonMissing`) : undefined}>
                  <Textarea rows={2} maxLength={500} value={why} onChange={(event) => { setWhy(event.target.value); setMissing(false); }} />
                </Field>
                <Button variant="danger" loading={mutations.publish.isPending && why.length > 0} onClick={override}>{t(`${R}.publish.overrideConfirm`)}</Button>
              </>
            ) : (
              <p className="text-xs text-fg-muted">{t(`${R}.publish.noOverride`)}</p>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
